import { useState, useRef } from 'react';
import { Button, Text, ActionIcon, Popover, Divider, UnstyledButton, Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconDots } from '@tabler/icons-react';
import { conversationUtils } from '../../../../../api';
import { ConversationOptionsModal } from '../ConversationOptionsModal';
import { RenameModal } from '../RenameModal';
import { DeleteConfirmationModal } from '../DeleteConfirmationModal';
import { useConversationStore } from '../../../../../stores/conversationStore';
import { useUserSettingsStore } from '../../../../../stores/userSettingsStore';
import { useShallow } from 'zustand/react/shallow';
import type { Conversation } from '../../../../../api/conversations';
import { useIsMessageSending } from '../../../../../hooks';
import styles from './ConversationItem.module.css';
import { trackComponentInteraction } from '../../../../../utils/analytics';
import { NAVBAR_FEATURES } from '../../../../../config/featureFlags';
import { useConversationDateLastAccessed } from '../../../../../hooks/useConversationDateLastAccessed';
import { useNavbarExpansion } from '../../../../../hooks'; 



export interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onChatClick: (conversationId: string) => void;
}

export function ConversationItem({ conversation, isActive, onChatClick }: ConversationItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { setExpanded, isNavBarLocked, setNavBarLocked } = useNavbarExpansion();
  


  // Check if any message is currently being sent across the entire app
  const isMessageSending = useIsMessageSending();

  const { isConversationPinned, togglePinnedConversation } = useUserSettingsStore();
  const { updateDateLastAccessed } = useConversationDateLastAccessed(conversation.conversationID);

  const {
    openModalConversationId,
    openModal,
    closeModal
  } = useConversationStore(useShallow((state) => ({
    openModalConversationId: state.openModalConversationId,
    openModal: state.openModal,
    closeModal: state.closeModal,
  })));

  const handleChatClick = () => {
    trackComponentInteraction(
      'ConversationItem',
      conversation.conversationTitle,
      'button',
      'click',
      'select_conversation'
    );
    onChatClick(conversation.conversationID);
  };

  const handleOptionsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    trackComponentInteraction(
      'ConversationItem',
      'Options Menu',
      'button',
      'click',
      'open_options'
    );
    setPopoverOpened(prev => !prev); // Toggle state: open when closed, close when open
  };

  const handlePopoverClose = () => {
    setPopoverOpened(false);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Only close popover if it's not explicitly opened by click
    // This prevents the popover from closing when hovering away briefly
  };

  const isMobile = useMediaQuery('(max-width: 48em)');

  // Computed state for button visibility
  // Show when hovered, popover open, any modal open, or on mobile when item is active (no hover on mobile)
  const shouldShowOptionsButton =
    isHovered ||
    popoverOpened ||
    isRenameModalOpen ||
    isDeleteModalOpen ||
    (isMobile && isActive);

  const prevNavBarLockedRef = useRef<boolean>(isNavBarLocked);

  const handleNavBarLock = () => {
    // capture current value synchronously
    prevNavBarLockedRef.current = isNavBarLocked;
    setNavBarLocked(true);
  };

  const handleNavBarUnlock = () => {
    // only restore if it was previously unlocked
    if (!prevNavBarLockedRef.current) {
      // defer to next tick to avoid synchronous nested updates
      setTimeout(() => { setNavBarLocked(false); }, 0);
      setTimeout(() => { setExpanded(false); }, 0);
    }
  };

  const handleRename = () => {
    setIsRenameModalOpen(true);
    handlePopoverClose();
    handleNavBarLock();
  };

  const handlePin = async () => {
    handleNavBarLock();
    updateDateLastAccessed();
    try {
      const isPinned = isConversationPinned(conversation.conversationID);
      trackComponentInteraction(
        'PinConversationItem',
        conversation.conversationTitle,
        'conversation',
        isPinned ? 'unpin' : 'pin',
        isPinned ? 'conversation_unpinned' : 'conversation_pinned'
      );
      togglePinnedConversation(conversation.conversationID, conversation.conversationTitle);
    } finally {
      handleNavBarUnlock();
    }
  };

  const handleRenameSubmit = (newTitle: string) => {
    // TODO: Implement actual rename logic
    setIsRenameModalOpen(false);
    setIsHovered(false);
    handleNavBarUnlock();
  };

  const handleRenameCancel = () => {
    setIsRenameModalOpen(false);
    setIsHovered(false);
    handleNavBarUnlock();
  };


  const handleDelete = () => {
    setIsDeleteModalOpen(true);
    handlePopoverClose();
    handleNavBarLock();
  };

  const handleDeleteConfirm = () => {
    // Modal handles the actual deletion and calls this on success
    setIsDeleteModalOpen(false);
    setIsHovered(false); // Reset hover state on modal close

    handleNavBarUnlock();
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setIsHovered(false); // Reset hover state on modal close

    handleNavBarUnlock();
  };

  return (
    <>
      <div
        className={[
          styles.conversationItem,
          isActive && styles.conversationItemActive,
          popoverOpened && styles.popoverOpen,
          isRenameModalOpen && styles.renameModalOpen,
          isDeleteModalOpen && styles.deleteModalOpen
        ].filter(Boolean).join(' ')}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
      >
        <Button
          variant={isActive ? "filled" : "subtle"}
          justify="flex-start"
          onClick={handleChatClick}
          disabled={isMessageSending}
          classNames={{
            root: styles.chatButton
          }}
        >
          <Text size="sm" lineClamp={2} className={styles.conversationTitle}>
            {conversationUtils.getDisplayTitle(conversation, 60)}
          </Text>
        </Button>

        <Popover
          width={240}
          position="right-start"
          opened={popoverOpened}
          onChange={setPopoverOpened}
          onClose={handlePopoverClose}
          closeOnClickOutside
          closeOnEscape
          withinPortal
          styles={{
            dropdown: {
              border: '1px solid var(--fonterra-border-accent)',
              borderRadius: '9px',
              backgroundColor: 'var(--fonterra-surface-popover)',
              boxShadow: 'var(--fonterra-shadow-popover)',
              padding: '12px 0px',
              minWidth: '168px',
            }
          }}
        >
          <Popover.Target>
            <div className={styles.optionsButtonWrapper}>
              {shouldShowOptionsButton && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={handleOptionsClick}
                  disabled={isMessageSending}
                  className={styles.optionsButton}
                  aria-label="Conversation options"
                >
                  <IconDots size={16} />
                </ActionIcon>
              )}
            </div>
          </Popover.Target>

            <Popover.Dropdown>
              <div className={styles.popoverContent}>
                <UnstyledButton
                  onClick={handleRename}
                  className={styles.popoverAction}
                >
                  <Text className={styles.actionText}>Rename</Text>
                </UnstyledButton>

                {NAVBAR_FEATURES.showPinnedChats && (
                    <UnstyledButton
                      onClick={handlePin}
                      className={styles.popoverAction}
                    >
                      <Text className={styles.actionText}>{isConversationPinned(conversation.conversationID) ? "Unpin" : "Pin"}</Text>
                    </UnstyledButton>
                )}

                <div className={styles.dividerContainer}>
                  <Divider className={styles.divider} />
                </div>

                <UnstyledButton
                  onClick={handleDelete}
                  className={styles.popoverAction}
                >
                  <Text className={styles.actionText}>Delete</Text>
                </UnstyledButton>
              </div>
            </Popover.Dropdown>
        </Popover>
      </div>

      <ConversationOptionsModal
        conversationId={conversation.conversationID}
        opened={openModalConversationId === conversation.conversationID}
        onClose={closeModal}
        onRename={handleRename}
        onDelete={handleDelete}
      />

      {/* Rename Modal */}
      <Modal
        opened={isRenameModalOpen}
        onClose={handleRenameCancel}
        size="auto"
        centered
        withCloseButton={false}
        padding={0}
        styles={{
          content: {
            backgroundColor: 'transparent',
          },
          body: {
            padding: 0,
          }
        }}
      >
        <RenameModal
          conversationId={conversation.conversationID}
          currentTitle={conversationUtils.getDisplayTitle(conversation, 200)}
          onRename={handleRenameSubmit}
          onCancel={handleRenameCancel}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        size="auto"
        centered
        withCloseButton={false}
        padding={0}
        styles={{
          content: {
            backgroundColor: 'transparent',
          },
          body: {
            padding: 0,
          }
        }}
      >
        <DeleteConfirmationModal
          conversationId={conversation.conversationID}
          onDelete={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      </Modal>
    </>
  );
}
